import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { adminAPI } from "../api";
import { getUserErrorMessage } from "../utils/errors";
import MIcon from "../icons/MIcon";
import SpinnerIcon from "../icons/SpinnerIcon";

const CreateAllowedUser = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [orgId, setOrgId] = useState("");
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isLoading, setIsLoading] = useState(false);

  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);

  const [allowedUsers, setAllowedUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");

  const fetchOrgs = useCallback(async () => {
    setOrgsLoading(true);
    try {
      const res = await adminAPI.listOrgs();
      setOrgs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error loading organizations:", err);
      }
      setMessage({ text: getUserErrorMessage(err, "load"), type: "error" });
      setOrgs([]);
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  const fetchAllowedUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError("");
    try {
      const res = await adminAPI.listAllowedUsers();
      // Backend returns AllowedUserResponse[]: [{ id, user, org_id }, ...]
      setAllowedUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setUsersError(getUserErrorMessage(err, "load"));
      setAllowedUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrgs();
    fetchAllowedUsers();
  }, [fetchOrgs, fetchAllowedUsers]);

  /* Quick lookup map: org_id -> org object, so the table doesn't have
     to do a linear scan for every row. */
  const orgsById = useMemo(() => {
    const map = new Map();
    for (const o of orgs) map.set(o.id, o);
    return map;
  }, [orgs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: "", type: "" });

    if (!username.trim()) {
      setMessage({ text: t("allowedUsers.errors.usernameRequired"), type: "error" });
      return;
    }

    if (!orgId) {
      setMessage({ text: t("allowedUsers.errors.orgRequired"), type: "error" });
      return;
    }

    setIsLoading(true);
    try {
      await adminAPI.createAllowedUser({
        user: username.trim(),
        org_id: parseInt(orgId, 10),
      });
      setMessage({
        text: t("allowedUsers.addedSuccess"),
        type: "success",
      });
      setUsername("");
      setOrgId("");
      // Refresh table so the new user shows up immediately
      fetchAllowedUsers();
    } catch (err) {
      setMessage({
        text: getUserErrorMessage(err, "create"),
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-4 sm:px-8 py-10">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* ── Page Header ── */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-brand dark:bg-brand flex items-center justify-center shadow-brand-glow">
            <MIcon name="group_add" className="text-white !text-[24px]" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold text-on-surface-light dark:text-on-surface tracking-tight">
              {t("nav.allowedUsers")}
            </h1>
            <p className="text-sm text-on-surface-light-variant dark:text-on-surface-variant">
              {t("allowedUsers.subtitle")}
            </p>
          </div>
        </div>

        {/* ── Create Allowed-User Card ── */}
        <div className="flat-card rounded-xl animate-float-in">
          <div className="p-6 sm:p-8">
            <h2 className="font-display text-base font-semibold text-on-surface-light dark:text-on-surface mb-4">
              {t("allowedUsers.authorizeNew")}
            </h2>

            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-start"
            >
              {/* Username */}
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                  <MIcon name="person" className="sm" />
                </div>
                <input
                  type="text"
                  placeholder={t("login.username")}
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="auth-input w-full pl-11 pr-4 py-3 rounded-lg text-sm font-body"
                />
              </div>

              {/* Organization Dropdown */}
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none z-10">
                  <MIcon name="corporate_fare" className="sm" />
                </div>
                <select
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                  required
                  disabled={orgsLoading}
                  className="auth-input glass-select w-full pl-11 pr-9 py-3 rounded-lg text-sm font-body appearance-none cursor-pointer"
                >
                  <option value="" disabled>
                    {orgsLoading
                      ? t("organizations.loading")
                      : t("allowedUsers.selectOrg")}
                  </option>
                  {orgs.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.orgname} ({org.phone})
                    </option>
                  ))}
                </select>
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                  <MIcon name="expand_more" className="sm" />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || orgsLoading}
                className="bg-brand dark:bg-brand-container hover:bg-brand-light dark:hover:bg-brand text-white font-semibold py-3 px-5 rounded-lg transition-all duration-200 shadow-brand-glow disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm tracking-wide active:scale-95 whitespace-nowrap"
              >
                {isLoading ? (
                  <>
                    <SpinnerIcon />
                    <span>{t("allowedUsers.adding")}</span>
                  </>
                ) : (
                  <>
                    <MIcon name="add" className="sm" />
                    <span>{t("allowedUsers.addUser")}</span>
                  </>
                )}
              </button>

              {/* Message Banner (full-width under the form row) */}
              {message.text && (
                <div
                  className={`
                    sm:col-span-3 rounded-lg px-4 py-3 text-sm flex items-start gap-2.5
                    ${
                      message.type === "success"
                        ? "bg-emerald-500/10 dark:bg-emerald-400/10 border border-emerald-500/20 dark:border-emerald-400/20 text-emerald-600 dark:text-emerald-400"
                        : "bg-red-500/10 dark:bg-red-400/10 border border-red-500/20 dark:border-red-400/20 text-red-600 dark:text-red-400"
                    }
                  `}
                >
                  <span className="mt-0.5 shrink-0">
                    <MIcon
                      name={
                        message.type === "success" ? "check_circle" : "error"
                      }
                      className="sm"
                    />
                  </span>
                  <span>{message.text}</span>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* ── Allowed Users List ── */}
        <div className="flat-card rounded-xl">
          <div className="p-6 sm:p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-base font-semibold text-on-surface-light dark:text-on-surface">
                {t("allowedUsers.authorizedUsers")}
                {!usersLoading && (
                  <span className="ml-2 text-xs font-normal text-on-surface-light-variant dark:text-on-surface-variant">
                    ({allowedUsers.length})
                  </span>
                )}
              </h2>
              <button
                type="button"
                onClick={fetchAllowedUsers}
                disabled={usersLoading}
                className="flex items-center gap-1.5 text-xs text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface transition-colors disabled:opacity-40"
              >
                <MIcon name="refresh" className="sm" />
                <span>{t("common.refresh")}</span>
              </button>
            </div>

            {/* States */}
            {usersLoading ? (
              <div className="flex items-center justify-center py-12 text-on-surface-light-variant dark:text-on-surface-variant">
                <SpinnerIcon />
                <span className="ml-2 text-sm">{t("allowedUsers.loading")}</span>
              </div>
            ) : usersError ? (
              <div className="rounded-lg px-4 py-3 text-sm flex items-start gap-2.5 bg-red-500/10 dark:bg-red-400/10 border border-red-500/20 dark:border-red-400/20 text-red-600 dark:text-red-400">
                <span className="mt-0.5 shrink-0">
                  <MIcon name="error" className="sm" />
                </span>
                <span>{usersError}</span>
              </div>
            ) : allowedUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-on-surface-light-variant dark:text-on-surface-variant">
                <MIcon name="inbox" className="!text-[32px] mb-2 opacity-60" />
                <p className="text-sm">{t("allowedUsers.empty")}</p>
                <p className="text-xs opacity-70">
                  {t("allowedUsers.emptyHint")}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-on-surface-light-variant dark:text-on-surface-variant border-b border-surface-light-highest dark:border-white/5">
                      <th className="px-3 py-2 font-medium w-16">{t("organizations.colId")}</th>
                      <th className="px-3 py-2 font-medium">{t("allowedUsers.colUsername")}</th>
                      <th className="px-3 py-2 font-medium">{t("allowedUsers.colOrganization")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allowedUsers.map((au) => {
                      const org = orgsById.get(au.org_id);
                      return (
                        <tr
                          key={au.id}
                          className="border-b border-surface-light-highest/60 dark:border-white/5 last:border-0 hover:bg-surface-light-container dark:hover:bg-surface-container transition-colors"
                        >
                          <td className="px-3 py-3 text-on-surface-light-variant dark:text-on-surface-variant font-mono text-xs">
                            #{au.id}
                          </td>
                          <td className="px-3 py-3 text-on-surface-light dark:text-on-surface font-medium">
                            {au.user}
                          </td>
                          <td className="px-3 py-3 text-on-surface-light-variant dark:text-on-surface-variant">
                            {org ? (
                              <span>
                                {org.orgname}{" "}
                                <span className="opacity-60 text-xs">
                                  ({org.phone})
                                </span>
                              </span>
                            ) : (
                              <span className="opacity-60 italic">
                                {t("allowedUsers.orgFallback", { id: au.org_id })}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAllowedUser;
